/**
 * Empty-state resolution for ProgramMarketView (DEV-440 / DEV-231).
 *
 * MarketView renders blank for several unrelated reasons and the backend
 * reports all of them identically as `{products: [], total: 0}`. Telling every
 * one of those users to "select a solution family" is actively harmful: a user
 * who already accepted a family reads it as their own mistake, stops reporting
 * the bug, and the real fault stays invisible.
 *
 * The distinguishing signals are already on the client. The taxonomy fetch
 * returns every family with its `status` and its anchor `technology_id`
 * (SolutionFamilySerializer exposes both; `technology_id` is nullable), so the
 * page can tell these apart without a second request:
 *
 * - nothing was ever proposed, so research is the missing step;
 * - families were proposed but none accepted, so review is the missing step
 *   (confirmed by ryan's user testing, 2026-08-18: this is the common case);
 * - a family is accepted but has no anchor technology, so retrieval
 *   short-circuits to an empty list before any product is considered
 *   (`marketview_service.py:1012-1020`, `observation_population.py`
 *   `family_candidate_products` / `program_candidate_products`);
 * - everything is wired up and the catalogue simply has nothing tagged.
 *
 * This is a UX guard, not a fix for the underlying linkage gap.
 */

/**
 * A solution family as the taxonomy endpoint returns it.
 *
 * `status` is carried because "none accepted" and "none proposed" need
 * different copy: one asks the user to review, the other to run research.
 */
export interface SolutionFamily {
  id: string
  name: string
  status: string
  technology_id: number | null
  technology_name: string | null
}

export type MarketEmptyState =
  /** Families have not loaded yet, so the cause is not known. Say nothing. */
  | { kind: 'pending' }
  /** The taxonomy holds no families at all: research has not proposed any. */
  | { kind: 'no-families' }
  /**
   * Families exist but not one is accepted, so there is nothing to search
   * over. `reviewableCount` is how many are still awaiting a decision -
   * zero means every one was rejected or superseded, which needs research
   * re-run rather than review.
   */
  | { kind: 'families-unaccepted'; reviewableCount: number }
  /** The selected family has no anchor technology, so it has no product pool. */
  | { kind: 'family-unanchored'; familyName: string }
  /** "All" view, but not one accepted family has an anchor technology. */
  | { kind: 'all-unanchored' }
  /** Families are anchored and were searched; nothing is tagged under them. */
  | { kind: 'no-products'; familyName: string | null }

/** The families MarketView actually searches over. */
export function acceptedOf(families: SolutionFamily[]): SolutionFamily[] {
  return families.filter((f) => f.status === 'accepted')
}

/**
 * Classify why MarketView came back empty.
 *
 * Only ever called once results are known to be empty: it explains a blank
 * screen, it does not decide whether the screen is blank.
 *
 * Takes every family rather than only the accepted ones, because the
 * difference between "research proposed nothing" and "you have four families
 * waiting to be accepted" is invisible once the list has been filtered - and
 * that difference is the whole point of the second state.
 *
 * `familiesLoaded` matters because the families fetch and the search are
 * independent requests. The search can return first - most easily for an
 * unanchored family, where the backend short-circuits before touching any
 * product table - and an empty list would then be read as "none accepted" when
 * it only means "not back yet". Guessing during that window shows the one
 * message a user with families must never see.
 */
export function resolveEmptyState(
  families: SolutionFamily[],
  activeFamilyId: string | null,
  familiesLoaded: boolean = true,
): MarketEmptyState {
  if (!familiesLoaded && families.length === 0) {
    return { kind: 'pending' }
  }

  if (families.length === 0) {
    return { kind: 'no-families' }
  }

  const acceptedFamilies = acceptedOf(families)

  if (acceptedFamilies.length === 0) {
    // 'proposed' is the only status a user can still turn into 'accepted';
    // rejected and superseded families are decided and will not come back.
    return {
      kind: 'families-unaccepted',
      reviewableCount: families.filter((f) => f.status === 'proposed').length,
    }
  }

  if (activeFamilyId !== null) {
    const active = acceptedFamilies.find((f) => f.id === activeFamilyId)
    // An unknown id is already reset to "All" by the page; treat it as a plain
    // miss rather than inventing a cause we cannot support.
    if (active && active.technology_id === null) {
      return { kind: 'family-unanchored', familyName: active.name }
    }
    return { kind: 'no-products', familyName: active?.name ?? null }
  }

  if (acceptedFamilies.every((f) => f.technology_id === null)) {
    return { kind: 'all-unanchored' }
  }

  return { kind: 'no-products', familyName: null }
}
